/**
 * Własny pomiar ruchu: co ludzie robią na stronie, bez śledzenia.
 *
 * Czego NIE robimy (na tym stoi polityka prywatności, rozdział o statystykach):
 * - nie ustawiamy ciasteczek i nic nie zapisujemy w przeglądarce. Jedyny wyjątek
 *   to wyłącznik, który użytkownik sam włącza adresem `?pomiar=wylacz`;
 * - nie wysyłamy ciasteczek z żądaniem (`credentials: 'omit'`), więc backend nie wie,
 *   czy wizyta należy do zalogowanego konta;
 * - nie wysyłamy parametrów adresu, tylko samą ścieżkę;
 * - szanujemy sygnały „nie śledź” (Do Not Track i Global Privacy Control).
 *
 * Wizyta to jedno wczytanie strony: identyfikator żyje tylko w pamięci tej karty.
 * Odświeżenie albo nowa karta to nowa wizyta, więc dwóch wizyt nie da się połączyć.
 *
 * Nowe zdarzenie wymaga wpisu w `Nazwa` w backendzie (`pomiar/models.py`),
 * inaczej backend je odrzuci. Projekt 3D (2026-10-13): `projekt3d_model` (cel: dach, grunt, bez_mapy),
 * `projekt3d_moduly`, `projekt3d_obraz`, `asystent_3d` (powrót do asystenta z danymi z modelu) i `dokument_pdf`
 * (cel: osd, psp_…, pme2_protokol, przez `pobranyDokument` z `lib/aktywnosc`). Bramki (2026-10-15): `bramka`
 * (cel: klucz bramki, np. projekt3d), gdy niezalogowany widzi zachętę zamiast narzędzia (`useZdarzenieBramki`).
 * „VoltValue dla domu” i zlecenie na Rynku (2026-10-24, pakiet P3): `dla_domu_droga` (cel: prosty, plan, oferta;
 * wejście z `/dla-domu`), `rynek_zlecenie` (cel: opcja sprzętu i liczba ofert, np. `zestaw_klient:6`), `rynek_kontakt`
 * (cel: ilu wykonawców dostało kontakt) i `teczka_do_zlecenia` (cel: projekt3d albo formularz, zlecenie z teczką P5).
 * Prosty dobór wokół celów (2026-10-24, pakiet P4): `dobor_cele` (cel: wybrane cele po przecinku, alfabetycznie),
 * `dobor_sprzet` (cel: wykonawca, zestaw_wykonawca, zestaw_klient) i `dobor_preferencje` (cel: dobor albo zapis,
 * „Zestaw według Twoich preferencji” po powrocie z Projektu 3D).
 * Ścieżka `/rynek` (2026-10-28, `hooks/usePomiarRynku` i `FormularzOgloszenia`): `rynek_czas` (cel: przedział czasu
 * na stronie), `rynek_przewiniecie` (cel: 25, 50, 75, 100), `rynek_krok` (cel: klik, kreator:<ekran>, zrodlo:<droga>,
 * projekt3d, zmien_odpowiedzi, wiecej, oferty:<n>, wystaw, okno, okno_zamkniete, wyslij, wyslane) i `rynek_blad`
 * (cel: pole, przy którym formularz pokazał brak). Do tego `rynek_kilka_pytan`, `rynek_zlecenie_gosc`
 * i `rynek_zrodlo_opisu`, które backend przyjmuje dopiero od tej daty.
 * Historia cen (2026-11-02): `historia_cen` (cel: kategoria i id modelu, np. `panel:123`), otwarcie wykresu.
 *
 * Osobno działa dziennik aktywności kont firmowych (`lib/aktywnosc.js`): ten wie, kto klika, i dotyczy tylko
 * zalogowanych firm. Ten plik zostaje anonimowy.
 */

import { apiFetch } from '../api/apiFetch'
const ADRES = `${process.env.REACT_APP_API_URL || '/api/'}v1/pomiar/`
const KLUCZ_WYLACZNIKA = 'vv_pomiar'

const losowaWizyta = () => {
  const znaki = 'abcdefghijklmnopqrstuvwxyz0123456789'
  const bajty = new Uint8Array(12)
  if (window.crypto && window.crypto.getRandomValues) window.crypto.getRandomValues(bajty)
  else for (let i = 0; i < bajty.length; i += 1) bajty[i] = Math.floor(Math.random() * 256)
  return Array.from(bajty, (b) => znaki[b % znaki.length]).join('')
}

const WIZYTA = losowaWizyta()
let pierwszaOdslona = true
let ostatniaSciezka = null

const bezWww = (host) => (host || '').toLowerCase().replace(/^www\./, '')

/** Wyłącznik na życzenie: `?pomiar=wylacz` i `?pomiar=wlacz`. Przydaje się właścicielowi,
 *  żeby własne klikanie nie zawyżało liczb. */
const wylaczony = () => {
  try {
    const par = new URLSearchParams(window.location.search).get('pomiar')
    if (par === 'wylacz') window.localStorage.setItem(KLUCZ_WYLACZNIKA, 'wylaczony')
    if (par === 'wlacz') window.localStorage.removeItem(KLUCZ_WYLACZNIKA)
    if (window.localStorage.getItem(KLUCZ_WYLACZNIKA) === 'wylaczony') return true
  } catch {
    // Zablokowana pamięć przeglądarki nie może wyłączać pomiaru ani psuć strony.
  }
  const nav = window.navigator || {}
  return nav.doNotTrack === '1' || window.doNotTrack === '1' || nav.globalPrivacyControl === true
}

const urzadzenie = () => {
  const w = window.innerWidth || 0
  if (w < 768) return 'telefon'
  if (w < 1024) return 'tablet'
  return 'komputer'
}

/** Skąd przyszła wizyta: `utm_source`, host strony odsyłającej albo „bezposrednio”. */
const zrodloWizyty = () => {
  try {
    const utm = new URLSearchParams(window.location.search).get('utm_source')
    if (utm) return utm.slice(0, 100)
    if (document.referrer) {
      const host = bezWww(new URL(document.referrer).hostname)
      if (host && host !== bezWww(window.location.hostname)) return host
    }
  } catch {
    // Nieczytelny referer traktujemy jak brak.
  }
  return 'bezposrednio'
}

/**
 * Wysyła jedno zdarzenie. Nigdy nie rzuca i nie czeka: pomiar nie może spowolnić
 * ani zepsuć strony, a jego błąd nie interesuje użytkownika.
 */
export const zdarzenie = (nazwa, cel = '', { zrodlo = '', sciezka = '' } = {}) => {
  try {
    if (wylaczony()) return
    apiFetch(ADRES, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
      // Przeżywa przejście na inną stronę (klik w ofertę sklepu).
      keepalive: true,
      body: JSON.stringify({
        n: nazwa,
        // `sciezka`: strona, której dotyczy zdarzenie, gdy adres już się zmienił (czas na /rynek przy wyjściu).
        s: sciezka || window.location.pathname,
        w: WIZYTA,
        z: zrodlo,
        c: String(cel || '').slice(0, 100),
        u: urzadzenie(),
      }),
    }).catch(() => {})
  } catch {
    // jak wyżej: pomiar nigdy nie przerywa działania strony
  }
}

/** Odsłona: wołana przy każdej zmianie ścieżki (patrz `PomiarOdslon`). */
export const odslona = (sciezka) => {
  // Ta sama ścieżka drugi raz z rzędu to nie nowa odsłona: tak zachowuje się
  // podwójny efekt w trybie deweloperskim Reacta i zmiana samych filtrów w adresie.
  if (sciezka === ostatniaSciezka) return
  ostatniaSciezka = sciezka
  const zrodlo = pierwszaOdslona ? zrodloWizyty() : ''
  pierwszaOdslona = false
  zdarzenie('odslona', '', { zrodlo })
}

/**
 * Jeden nasłuch na cały dokument zamiast dopisywania pomiaru do każdego linku:
 * linków do sklepów jest kilkanaście rodzajów w różnych komponentach.
 * Rejestruje klik w link zewnętrzny (`wyjscie`, cel = host) i w adres e-mail (`kontakt`).
 */
export const nasluchujWyjsc = () => {
  const obsluz = (e) => {
    if (e.type === 'auxclick' && e.button !== 1) return
    const a = e.target && e.target.closest ? e.target.closest('a[href]') : null
    if (!a) return
    const href = a.getAttribute('href') || ''
    if (href.startsWith('mailto:')) {
      zdarzenie('kontakt')
      return
    }
    let url
    try { url = new URL(href, window.location.href) } catch { return }
    if (!/^https?:$/.test(url.protocol)) return
    if (bezWww(url.hostname) === bezWww(window.location.hostname)) return
    zdarzenie('wyjscie', bezWww(url.hostname))
  }
  document.addEventListener('click', obsluz, true)
  document.addEventListener('auxclick', obsluz, true)
  return () => {
    document.removeEventListener('click', obsluz, true)
    document.removeEventListener('auxclick', obsluz, true)
  }
}
